import { Button, DatePicker, Form, Select } from "antd";
import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { authenticateToken } from "../../utils/auth";
import { getPage, getParamValue, OrderTypeArray } from "../../utils/index";
import Loading from "../common/Loader";
import Empty from "../common/Empty";
import moment from "moment";
import TopHeading from "./TopHeading";
import { debounce } from "lodash";
import { useLocation } from "react-router";
import PaginationTwo from "../common/PaginationTwo";
const { RangePicker } = DatePicker;
const { Option } = Select;

const RevenueReportList = () => {
  const [form] = Form.useForm();
  const [status, setStatus] = useState<any>();
  const [limit, setLimit] = useState(16);
  const loc = useLocation();
  const page = getParamValue(loc.search, "page");
  const [paymentMethod, setPaymentMethod] = useState<any>();
  const [type, setType] = useState("JC_MART");
  const [hubId, setHubId] = useState<any>();
  const [shopId, setShopId] = useState<any>();

  const [range, setRange] = useState({
    from: null,
    to: null,
  });

  const [ordersData, setOrdersData] = useState({
    loading: false,
    data: null,
  });

  const [downloadData, setDownloadData] = useState({
    loading: false,
    data: null,
  });

  const [hubOptions, setHubOptions] = useState({
    loading: false,
    list: [],
  });

  const [shopOptions, setShopOptions] = useState({
    loading: false,
    list: [],
  });

  const [methodOptions, setMethodOptions] = useState({
    loading: false,
    list: null,
  });

  const getRevenueReport = useCallback(async () => {
    const from = moment(range.from)?.toISOString();
    const to = moment(range.to)?.toISOString();

    setOrdersData({ loading: true, data: null });
    const encodedUri = `${process.env.REACT_APP_ORDER_API}`;
    axios
      .get(
        `${encodedUri}/finance/revenue?page=${page || 0}&limit=${limit || 16}` +
          (from ? `&fromDate=${from}` : ``) +
          (to ? `&toDate=${to}` : ``) +
          (hubId ? `&hubId=${hubId}` : ``) +
          (shopId ? `&shopId=${shopId}` : ``) +
          (paymentMethod ? `&paymentMethod=${paymentMethod}` : ``) +
          (status ? `&status=${status}` : ``),
        {
          headers: {
            Authorization: `Bearer ${authenticateToken()}`,
          },
        }
      )
      .then((res) => {
        setOrdersData({ loading: false, data: res.data });
      })
      .catch((err) => {
        setOrdersData({ loading: false, data: [] });
        console.error("Data: Error", err);
      });
  }, [hubId, paymentMethod, range.from, range.to, shopId, status, page, limit]);

  const getRevenueReportDownload = useCallback(async () => {
    const from = moment(range.from)?.toISOString();
    const to = moment(range.to)?.toISOString();

    setDownloadData({ loading: true, data: null });
    const encodedUri = `${process.env.REACT_APP_ORDER_API}`;
    fetch(
      `${encodedUri}/finance/revenue/download` +
        (from ? `?fromDate=${from}` : ``) +
        (to ? `&toDate=${to}` : ``) +
        (hubId ? `&hubId=${hubId}` : ``) +
        (shopId ? `&shopId=${shopId}` : ``) +
        (paymentMethod ? `&paymentMethod=${paymentMethod}` : ``) +
        (status ? `&status=${status}` : ``),
      {
        headers: {
          Authorization: `Bearer ${authenticateToken()}`,
        },
      }
    )
      .then((response) => {
        return response.blob();
      })
      .then((res) => {
        setDownloadData({ loading: false, data: res });
        const url = window.URL.createObjectURL(new Blob([res]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Revenue_Report-${moment(range.from).format("YYYY_MM_DD")}-${moment(
            range.to
          ).format("YYYY_MM_DD")}.xlsx`
        );
        link.click();
        // saveAs(res, `revenue-report-${Math.random().toString(36)}.xlsx`);
      })
      .catch((err) => {
        console.error("Data: Error", err);
      });
  }, [hubId, paymentMethod, range.from, range.to, shopId, status]);

  //   ************************************
  //   ************************************

  const getHubOptions = useCallback(
    async (key) => {
      setHubOptions({ loading: true, list: null });
      const encodedUri = `${process.env.REACT_APP_CATALOG_READER_API}`;
      return axios
        .get(
          `${encodedUri}/hub/search?page=0&limit=20` +
            (key ? `&name=${key}` : ``) +
            (type ? `&type=${type}` : ``),
          {
            headers: {
              Authorization: `Bearer ${authenticateToken()}`,
            },
          }
        )
        .then((res) => {
          setHubOptions({
            loading: false,
            list: res.data?.hub?.map((hub) => ({
              value: hub.id,
              label: hub.name,
            })),
          });
        })
        .catch((err) => {
          setHubOptions({ loading: false, list: [] });
          console.error("Hubs: Error", err);
        });
    },
    [type]
  );

  const getShopOptions = useCallback(
    async (key) => {
      setShopOptions({ loading: true, list: null });
      const encodedUri = `${process.env.REACT_APP_CATALOG_READER_API}`;
      return axios
        .get(
          `${encodedUri}/shop/admin/search?page=0&limit=20` +
            (hubId ? `&hubId=${hubId}` : ``) +
            (key ? `&key=${key}` : ``) +
            (type ? `&type=${type}` : ``),
          {
            headers: {
              Authorization: `Bearer ${authenticateToken()}`,
            },
          }
        )
        .then((res) => {
          setShopOptions({
            loading: false,
            list: res.data?.shops?.map((shop) => ({
              value: shop.id,
              label: shop.name,
            })),
          });
        })
        .catch((err) => {
          setShopOptions({ loading: false, list: [] });
          console.error("Shops: Error", err);
        });
    },
    [type, hubId]
  );

  const getMethodOptions = useCallback(async () => {
    setMethodOptions({ loading: true, list: null });
    const encodedUri = `${process.env.REACT_APP_PAY_API}`;
    axios
      .get(
        `${encodedUri}/payment-method?page=${page || 0}&limit=${limit || 16}` +
          (type ? `&type=${type}` : `JC_MART`) +
          (status ? `&status=${status}` : ``),
        {
          headers: {
            Authorization: `Bearer ${authenticateToken()}`,
          },
        }
      )
      .then((res) => {
        setMethodOptions({
          loading: false,
          list: res.data?.methods?.map((method) => ({
            label: method.title,
            value: method.name,
          })),
        });
      })
      .catch((err) => {
        setMethodOptions({ loading: false, list: [] });
        console.error("Method: Error", err);
      });
  }, [limit, status, page, type]);

  const handleChangeStatus = (value: string) => {
    setStatus(value);
  };

  const fetchRef = useRef(null);
  const handleSearch = React.useMemo(() => {
    const loadOptions = (value: string, field: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;

      if (fetchId !== fetchRef.current) {
        return;
      }
      if (value) {
        if (field === "hub") getHubOptions(value);
        else if (field === "shop") getShopOptions(value);
      }
    };

    return debounce(loadOptions, 800);
  }, [getHubOptions, getShopOptions]);

  //   ************************************
  //   ************************************

  useEffect(() => {
    getRevenueReport();
  }, [getRevenueReport]);

  const reseAllFieldData = () => {
    setType("");
    setHubId("");
    setShopId("");
    form.resetFields();
    setRange({
      from: null,
      to: null,
    });
  };

  const totalRevenue = useMemo(() => {
    return (
      ordersData?.data?.orders?.reduce((obj, data) => {
        const newObj = {};
        const onlyMethods = { ...obj, ...newObj };
        let allMethodsTotal = data?.revenue;
        Object.keys(onlyMethods)?.forEach((item) => {
          allMethodsTotal += onlyMethods?.[item];
        });

        return {
          ...onlyMethods,
          total: allMethodsTotal,
        };
      }, {}) || {}
    );
  }, [ordersData]);

  useEffect(() => {
    getHubOptions("");
    getShopOptions("");
    getMethodOptions();
  }, [getHubOptions, getShopOptions, getMethodOptions]);

  useEffect(() => {
    if (hubId) {
      getShopOptions(``);
      setShopId(undefined);
      form.resetFields(["shop_search"]);
    }
  }, [form, getShopOptions, hubId]);

  return (
    <React.Fragment>
      <TopHeading
        {...ordersData?.data?.totalElements}
        refetch={getRevenueReport}
        title={"Revenue Report"}
        download={() => getRevenueReportDownload()}
        dissable={!ordersData?.data?.orders?.length ? true : false}
        loading={downloadData?.loading}
      />
      <section className="search_area">
        <Form form={form} className="search_form">
          <Form.Item name="range">
            <RangePicker
              defaultValue={
                range.from != null && range.to != null
                  ? [moment(range.from), moment(range.to)]
                  : null
              }
              onChange={(e, f) => {
                console.log("momnet", moment.utc(f[0])?.toISOString());
                setRange({ from: f?.[0], to: f?.[1] });
              }}
              showTime={{
                hideDisabledOptions: true,
                defaultValue: [
                  moment("00:00:00", "HH:mm:ss"),
                  moment("23:59:59", "HH:mm:ss"),
                ],
              }}
              inputReadOnly
              allowClear
            />
          </Form.Item>
          <Form.Item name="hub_search" initialValue={hubId}>
            <Select
              allowClear
              showSearch
              placeholder="Filter by Hub"
              optionFilterProp="children"
              onChange={(val) => setHubId(val)}
              onSearch={(e) => handleSearch(e, "hub")}
              filterOption={(input, option) =>
                option.children
                  .toString()
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              // options={hubOptions?.list}
            >
              {hubOptions?.list?.length ? (
                <Option value="">All Hubs</Option>
              ) : undefined}
              {hubOptions?.list?.map((hub) => (
                <Option value={hub?.value}>{hub?.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="shop_search" initialValue={shopId}>
            <Select
              allowClear
              showSearch
              placeholder="Filter by Shop"
              optionFilterProp="children"
              onChange={(val) => setShopId(val)}
              onSearch={(e) => handleSearch(e, "shop")}
              filterOption={(input, option) =>
                option.children
                  .toString()
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {shopOptions?.list?.length ? (
                <Option value="">All Shops</Option>
              ) : undefined}
              {shopOptions?.list?.map((shop) => (
                <Option value={shop?.value}>{shop?.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="status_search" initialValue={status}>
            <Select
              showSearch
              placeholder="Filter by Status"
              onChange={handleChangeStatus}
            >
              <Select.Option value="">ALL STATUS</Select.Option>
              {Object.values(OrderTypeArray)?.map((type, i) => (
                <Select.Option value={type} key={i}>
                  {type?.split("_").join(" ")}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="method_search" initialValue={paymentMethod}>
            <Select
              allowClear
              showSearch
              placeholder="Payment Method"
              optionFilterProp="children"
              onChange={(val) => setPaymentMethod(val)}
              onSearch={(e) => handleSearch(e, "method")}
              filterOption={(input, option) =>
                option.children
                  .toString()
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {methodOptions?.list?.length ? (
                <Option value={""}>All Methods</Option>
              ) : undefined}
              {methodOptions?.list?.map((method, i) => (
                <Option value={method?.value} key={i}>
                  {method?.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
        <div className="search_btn">
          <Button type="primary" size="large" danger onClick={reseAllFieldData}>
            Reset
          </Button>
        </div>
      </section>

      <div className="product-list-item-area white-bg section-padding-bottom">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="product-list-items-inner-content">
                <div className="single-product-wrapper">
                  <div className="single-product-heading">
                    <span>Date</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Shop Name</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Order Id</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Status</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Payment Method</span>
                  </div>
                  <div className="single-product-heading">
                    <span>MRP</span>
                  </div>
                  <div className="single-product-heading">
                    <span>TP</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Revenue</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            {ordersData?.loading ? (
              <Loading />
            ) : (
              <div
                className="col-lg-12 product-table"
                style={{ maxHeight: `calc(100vh - 380px)` }}
              >
                <div className="product-table-area">
                  {ordersData?.data?.orders?.length ? (
                    ordersData?.data?.orders?.map(
                      (ordersData: any, index: any) => (
                        <React.Fragment key={index}>
                          <div className="single-product-wrapper">
                            <div className="single-product">
                              <span className="name">
                                {moment(ordersData?.date).format("lll")}
                              </span>
                            </div>
                            <div className="single-product">
                              <span className="name">
                                {ordersData?.shopName}
                              </span>
                            </div>
                            <div className="single-product">
                              <span className="name">
                                {ordersData?.orderId}
                              </span>
                            </div>

                            <div className="single-product">
                              <span
                                className={`radius-btn ${ordersData?.status?.toLowerCase()}-btn text-nowrap`}
                              >
                                {ordersData?.status?.split("_").join(" ")}
                              </span>
                            </div>
                            <div className="single-product">
                              <span className="name">
                                {ordersData?.paymentMethod}
                              </span>
                            </div>
                            <div className="single-product">
                              <span className="name">
                                {parseFloat(ordersData?.mrp)}
                              </span>
                            </div>
                            <div className="single-product">
                              <span className="name">
                                {parseFloat(ordersData?.tp)}
                              </span>
                            </div>
                            <div className="single-product">
                              <span
                                className={`name ${
                                  ordersData?.revenue <= 0
                                    ? `text-danger font-weight-bold`
                                    : ``
                                }`}
                              >
                                {ordersData?.revenue}
                              </span>
                            </div>
                          </div>
                        </React.Fragment>
                      )
                    )
                  ) : (
                    <Empty data="Data" />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="row">
            {ordersData?.loading ? (
              <Loading />
            ) : (
              <div className="col-lg-12">
                <div className="product-table-area ">
                  <div className="single-product-wrapper">
                    <div className="single-product">
                      <b className="name"></b>
                    </div>
                    <div className="single-product">
                      <b className="name"></b>
                    </div>
                    <div className="single-product">
                      <b className="name"></b>
                    </div>
                    <div className="single-product">
                      <b className="name"></b>
                    </div>
                    <div className="single-product">
                      <b className="name"></b>
                    </div>

                    <div className="single-product">
                      <b className="name font-weight-bold">Total</b>
                    </div>
                    <div className="single-product">
                      <span
                        className={`name font-weight-bold ${
                          totalRevenue?.total <= 0 ? `text-danger` : ``
                        }`}
                      >
                        &#2547;{parseInt(totalRevenue?.total) || 0}
                        {/* {console.log(totalRevenue)} */}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <PaginationTwo
            {...ordersData?.data}
            limit={limit}
            page={getPage(loc.search)}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default RevenueReportList;
